/**
 * Service d'orchestration du consentement RGPD.
 * Applique les préférences utilisateur aux services (tracking, Sentry, marketing).
 */
import * as Sentry from '@sentry/react'
import { initTracking, stopTracking, enableTracking, disableTracking, trackEvent } from './tracking'
import { captureUtm } from './utm'
import { CLE_ECHANTILLON } from './rumCarte'
import api from './api'

let sentryUpgraded = false

/**
 * Appliquer les préférences de consentement à tous les services.
 * Appelé au démarrage de l'app et après chaque changement de consentement.
 */
export function applyConsent({ analytique, marketing }) {
  // --- Mesure d'audience ---
  if (analytique) {
    enableTracking()
    initTracking()
    upgradeSentry()
    // Flusher les UTM bufferisées en mémoire (consentement tardif)
    const utmData = captureUtm()
    if (utmData) {
      trackEvent('landing.visit', utmData)
    }
  } else {
    disableTracking()
    stopTracking()
    clearTrackingStorage()
    downgradeSentry()
  }

  // --- Marketing (sync backend pour les utilisateurs authentifiés) ---
  syncMarketingConsent(marketing)
}

/**
 * Activer les fonctionnalités Sentry avancées (PII, replays, tracing).
 */
function upgradeSentry() {
  if (sentryUpgraded) return
  const client = Sentry.getClient()
  if (!client) return

  const options = client.getOptions()
  options.sendDefaultPii = true
  // Les intégrations replay et tracing sont ajoutées dynamiquement
  // Utiliser getIntegrationByName() car options.integrations ne reflète pas
  // les intégrations ajoutées dynamiquement via Sentry.addIntegration()
  if (!client.getIntegrationByName('Replay')) {
    Sentry.addIntegration(Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }))
  }
  if (!client.getIntegrationByName('BrowserTracing')) {
    Sentry.addIntegration(Sentry.browserTracingIntegration())
  }
  sentryUpgraded = true
}

/**
 * Revenir au mode Sentry minimal (erreurs seulement, pas de PII).
 */
function downgradeSentry() {
  if (!sentryUpgraded) return
  const client = Sentry.getClient()
  if (!client) return

  const options = client.getOptions()
  options.sendDefaultPii = false
  Sentry.setUser(null)
  sentryUpgraded = false
}

/**
 * Supprimer les données de tracking en sessionStorage.
 */
function clearTrackingStorage() {
  sessionStorage.removeItem('bonurba_session_id')
  sessionStorage.removeItem('bonurba_utm')
  sessionStorage.removeItem(CLE_ECHANTILLON)
}

/**
 * Synchroniser le consentement marketing avec le backend.
 * Fire-and-forget — ne bloque pas l'UX.
 * Exporté pour être appelé après authentification (login, register, refresh).
 */
export function syncMarketingConsent(accepted) {
  // Uniquement si l'utilisateur est authentifié
  const authHeader = api.defaults.headers.common['Authorization']
  if (!authHeader) return

  api.patch('/api/users/me/consent', {
    marketing_consent: !!accepted,
  }).catch(() => {
    // Silencieux : fire-and-forget
  })
}

/**
 * Vérifie si le consentement analytique est actif.
 */
export function hasAnalytiqueConsent() {
  try {
    const raw = localStorage.getItem('bonurba_consent')
    if (!raw) return false
    return JSON.parse(raw).analytique === true
  } catch {
    return false
  }
}
